import React, { memo, useMemo } from 'react';
import styled from '@emotion/styled';
import get from 'lodash/get';
import toLower from 'lodash/toLower';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import { getAppType, getLocale } from '@vmos2/core';
import { timeUtils, localTime, useDateFnsLocale } from '@vmos2/utils';
import { useDeviceSize, useSettings } from '@vmos2/providers';
import { actionsNames, analyticsService } from '@vmos2/analytics';
import { Button, Link, Title } from '@vmos2/design-system';

import { MenuStatus } from '../constants';

type Props = {
  menu: mixed[],
  isNameShown: boolean,
  isMenuStockAvailable: boolean,
  isDeliveryAvailable: boolean,
  isFutureDay?: boolean,
  storeUUID: string,
  storeName: string,
  menuOverviewPath: string,
  holidayHours?: [],
};

const getStatusColor = (theme, status) =>
  get(
    {
      [MenuStatus.OPEN]: theme.color.success,
      [MenuStatus.PREORDER]: theme.color.info,
      [MenuStatus.CLOSED]: theme.color.grey,
    },
    status,
    theme.color.brand
  );

export const Menu = memo(
  ({
    menu,
    isNameShown,
    isMenuStockAvailable,
    isDeliveryAvailable,
    isFutureDay,
    storeUUID,
    storeName,
    menuOverviewPath,
    holidayHours,
  }: Props) => {
    const { isMobile } = useDeviceSize();
    const { t } = useTranslation('storeSelection');
    const theme = useTheme();
    const { timezone } = useSettings();
    const analyticsOpenMenu = properties => {
      analyticsService.sendEvent(actionsNames.openMenu, { properties });
    };
    const l = getLocale();
    const { i18n } = useTranslation();
    const dateFnsLocale = useDateFnsLocale(i18n.language);
    const locale = i18n.language;
    const { getMenuHours } = timeUtils;

    const timeFormat = !locale || locale === 'en-GB' ? 'h:mma' : 'HH:mm';

    const isOpat = getAppType() === 'opat';
    const statusColor = getStatusColor(theme, menu.status);
    const isMenuClosed =
      menu.status === MenuStatus.CLOSED ||
      !isMenuStockAvailable ||
      (getAppType() === 'delivery' && isDeliveryAvailable);

    const menuOpeningHours = getMenuHours(menu, holidayHours, timezone, isFutureDay);

    const menuName = useMemo(() => l(menu, 'displayName') || l(menu, 'name'), [menu]);

    const btnText = isMenuClosed && !isOpat ? 'viewMenu' : 'order';
    const btnMode = !isMenuClosed
      ? get(
          {
            [MenuStatus.CLOSED]: 'light',
            [MenuStatus.PREORDER]: 'outline',
          },
          menu.status,
          'primary'
        )
      : 'light';

    const analyticsEvent = () => {
      const menuState = isMenuClosed ? 'closed' : toLower(menu.status);
      return analyticsOpenMenu({
        menuState,
        menuName,
        menuUUID: menu.uuid,
        storeName,
        storeUUID,
      });
    };

    return (
      <Wrapper data-test="menu-wrapper">
        <StoreInfo>
          {isNameShown && (
            <Title level={3} dataTest="menu-name">
              {menuName}
            </Title>
          )}
          <StoreHours>
            <Status color={statusColor}>{t(menu.status)}</Status>
            {menuOpeningHours && (
              <MenuHours data-test="menu-hours">
                {menuOpeningHours.map(hours =>
                  timeUtils
                    .formatHoursInterval(hours, 'HH:mm', timeFormat)
                    .toLowerCase()
                    .split(' ')
                    // eslint-disable-next-line react/no-array-index-key
                    .map((el, i) => <MenuHoursElement key={i}>{el}</MenuHoursElement>)
                )}
              </MenuHours>
            )}
          </StoreHours>
          {!isMenuStockAvailable && (
            <Warning data-test="menu-currently-unavailable">{t('storeSelection:currentlyUnavailable')}</Warning>
          )}
          {isFutureDay && (menu.futureWeekDay || menu.futureDay) && (
            <FutureDay data-test="available-from">
              {t('availableFrom', {
                day: menu.futureDay
                  ? localTime.format(menu.futureDay, 'DATE', locale, dateFnsLocale)
                  : menu.futureWeekDay,
              })}
            </FutureDay>
          )}
        </StoreInfo>
        <Action
          as={Link}
          to={menuOverviewPath}
          params={{ storeUUID, menuUUID: menu.uuid }}
          size={isMobile ? 'xsmall' : 'small'}
          mode={isOpat ? 'primary' : btnMode}
          analyticsEvent={analyticsEvent}
          ml="auto"
        >
          {t(btnText)}
        </Action>
      </Wrapper>
    );
  }
);

const Wrapper = styled('div')`
  display: flex;
  align-items: start;
  justify-content: space-between;
  border-top: 1px solid ${({ theme }) => theme.color.lightGrey};
  margin-top: 16px;
  padding-top: 16px;
`;

const StoreInfo = styled('div')`
  color: ${({ theme }) => theme.color.grey};
  font-size: ${({ theme }) => theme.fontSize.xs};
  margin-right: 4px;

  h3 {
    font-size: ${({ theme }) => theme.fontSize.sm};
    color: ${({ theme }) => theme.color.black};
    margin: 0 0 4px 0;
  }

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    font-size: ${({ theme }) => theme.fontSize.sm};

    h3 {
      font-size: ${({ theme }) => theme.fontSize.md};
    }
  }
`;

const Action = styled(Button)`
  flex: none;
  width: 40%;
  max-width: 180px;
  max-height: 64px;
  display: inline-block;
  text-align: center;
  overflow: hidden;

  @media (${({ theme }) => theme.breakpoint.largeScreenKiosk}) {
    width: 52%;
  }
`;

const Status = styled('div')`
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ color }) => color};
  margin-bottom: 4px;
`;

const Warning = styled('p')`
  color: ${({ theme }) => theme.color.danger};
  margin: 4px 0 0 0;
`;

const FutureDay = styled('p')`
  margin: 4px 0 0 0;
`;

const StoreHours = styled('div')`
  margin-bottom: 4px;
`;

const MenuHours = styled('div')`
  display: grid;
  grid-auto-flow: row;
  grid-template-columns: max-content max-content 1fr;
  gap: 4px;
`;

const MenuHoursElement = styled('div')`
  &:nth-of-type(3n-1) {
    justify-self: center;
  }
`;
